import React, { createContext, useContext, useState, useEffect } from 'react';
import { trackLanguageSwitch } from '../utils/analytics';

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within LanguageProvider');
  }
  return context;
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    // Get from localStorage or default to 'en'
    return localStorage.getItem('language') || 'en';
  });

  useEffect(() => {
    localStorage.setItem('language', language);
    document.documentElement.lang = language === 'sw' ? 'sw' : 'en';
  }, [language]);

  const translations = {
    en: {
      nav: {
        home: 'Home',
        services: 'Services',
        about: 'About',
        team: 'Team',
        testimonials: 'Testimonials',
        blog: 'Blog',
        shop: 'Shop',
        appointment: 'Appointment',
        contact: 'Contact',
        faq: 'FAQ'
      },
      hero: {
        subtitle: 'Professional Dental Care',
        title: 'Making Dentistry a Positive Experience',
        description: 'Expert dental care to keep your smile bright and healthy',
        bookAppointment: 'Book Appointment',
        ourServices: 'Our Services'
      },
      common: {
        learnMore: 'Learn More',
        readMore: 'Read More',
        bookNow: 'Book Appointment',
        contactUs: 'Contact Us',
        viewAll: 'View All',
        back: 'Back'
      },
      footer: {
        quickLinks: 'Quick Links',
        services: 'Services',
        contactInfo: 'Contact Info',
        rights: 'All rights reserved.'
      },
      pages: {
        aboutTitle: 'About Dr. Muyabin',
        aboutSubtitle: 'Dedicated to making dentistry a positive experience',
        servicesTitle: 'Our Dental Services',
        servicesSubtitle: 'Comprehensive care for your whole family',
        faqTitle: 'Frequently Asked Questions',
        faqSubtitle: 'Answers to common questions about our dental services',
        testimonialsTitle: 'Patient Testimonials',
        testimonialsSubtitle: 'See what our patients say about their experience with us'
      }
    },
    sw: {
      nav: {
        home: 'Nyumbani',
        services: 'Huduma',
        about: 'Kuhusu',
        team: 'Timu',
        testimonials: 'Maoni',
        blog: 'Blogu',
        shop: 'Duka',
        appointment: 'Miadi',
        contact: 'Wasiliana',
        faq: 'Maswali'
      },
      hero: {
        subtitle: 'Matibabu ya Meno Kina',
        title: 'Kufanya Matibabu ya Meno Kuwa Uzoefu Mzuri',
        description: 'Matibabu bora ya meno ili kuweka tabasamu lako kuwa gumu na la afya',
        bookAppointment: 'Panga Miadi',
        ourServices: 'Huduma Zetu'
      },
      common: {
        learnMore: 'Jifunze Zaidi',
        readMore: 'Soma Zaidi',
        bookNow: 'Panga Miadi',
        contactUs: 'Wasiliana Nasi',
        viewAll: 'Angalia Zote',
        back: 'Rudi'
      },
      footer: {
        quickLinks: 'Viungo vya Haraka',
        services: 'Huduma',
        contactInfo: 'Mawasiliano',
        rights: 'Haki zote zimehifadhiwa.'
      },
      pages: {
        aboutTitle: 'Kuhusu Dk. Muyabin',
        aboutSubtitle: 'Tumejitolea kufanya matibabu ya meno kuwa uzoefu mzuri',
        servicesTitle: 'Huduma Zetu za Meno',
        servicesSubtitle: 'Matibabu kamili kwa familia yako yote',
        faqTitle: 'Maswali Yanayoulizwa Mara kwa Mara',
        faqSubtitle: 'Majibu ya maswali ya kawaida kuhusu huduma zetu za meno',
        testimonialsTitle: 'Maoni ya Wagonjwa',
        testimonialsSubtitle: 'Soma kile wagonjwa wetu wanasema kuhusu uzoefu wao nasi'
      }
    }
  };

  const t = (key) => {
    const keys = key.split('.');
    let value = translations[language];
    for (const k of keys) {
      value = value?.[k];
    }
    return value || key;
  };

  const toggleLanguage = () => {
    setLanguage((prev) => {
      const next = prev === 'en' ? 'sw' : 'en';
      trackLanguageSwitch(next);
      return next;
    });
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, toggleLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};


